'use client';

import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import type { BlockRenderProps } from '../../types';
import { ContactMessage } from './contactChrome';
import { readContactView } from './contactView';

/**
 * Ledger: one ruled house card — address, way, phone, and the hours list.
 * No brand seal, social row, or legal bar. On wide viewports the message
 * sits as a peer column aligned to the section title; stacked below on small screens.
 */
export function ContactLedger({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const view = readContactView({ block, locale, isActive, activeEditTarget, editable });
  const { target } = view;
  const tel =
    view.telHref && !editable ? (
      <a className="ws-contact-ledger__phone" href={view.telHref}>
        {view.phone}
      </a>
    ) : (
      view.phone
    );

  return (
    <section
      className={['ws-contact-ledger', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-contact-arrangement="ledger"
      id="visit"
    >
      <div className="ws-contact-ledger__inner">
        <div className="ws-contact-ledger__house">
          <header className="ws-contact-ledger__header">
            <EditableContent
              as="span"
              fieldKey="sectionIcon"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact__icon-hit"
            >
              <LucideIcon icon={view.sectionIcon} size="lg" className="ws-contact__icon" />
            </EditableContent>
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact-ledger__title"
            >
              {view.sectionTitle}
            </EditableContent>
          </header>
          {view.intro || editable ? (
            <EditableContent
              fieldKey="intro"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact-ledger__intro"
            >
              {view.intro}
            </EditableContent>
          ) : null}

          <div className="ws-contact-ledger__card">
            {view.address || editable ? (
              <EditableContent
                fieldKey="address"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact-ledger__line ws-contact-ledger__line--address"
              >
                {view.address}
              </EditableContent>
            ) : null}
            {view.directions || editable ? (
              <EditableContent
                fieldKey="directions"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact-ledger__line ws-contact-ledger__line--way"
              >
                {view.directions}
              </EditableContent>
            ) : null}
            {view.phone || editable ? (
              <EditableContent
                as="div"
                fieldKey="phone"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact-ledger__line"
              >
                {editable ? view.phone : tel}
              </EditableContent>
            ) : null}
            {view.hoursRows.length > 0 || view.hours || editable ? (
              <EditableContent
                fieldKey="hours"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact-ledger__hours"
              >
                {view.hoursRows.length > 0 ? (
                  <div className="ws-hours-schedule">
                    {view.hoursRows.map((row) => (
                      <div className="ws-hours-schedule__row" key={`${row.day}-${row.time}`}>
                        <span className="ws-hours-schedule__day">{row.day}</span>
                        <span className="ws-hours-schedule__time">{row.time}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  view.hours
                )}
              </EditableContent>
            ) : null}
          </div>
        </div>

        <ContactMessage
          view={view}
          locale={locale}
          className="ws-contact-ledger__message"
          titleClass="ws-contact__form-title"
          supportClass="ws-contact__form-support"
        />
      </div>
    </section>
  );
}
